import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Form, Modal, Spinner } from '../../../components/ui';
import { materialCertificateService } from '../../../services/materialCertificateService';
import type { SiteWithLocations } from '../types';
import {
  CERTIFICATE_DESCRIPTION_MAX_LENGTH,
  CERTIFICATE_REFERENCE_MAX_LENGTH,
  attachedMessage,
  certifiableBins,
  todayIso,
  validateCertificateForm,
  type CertificateForm,
} from '../utils/certificates';

interface AttachCertificateModalProps {
  show: boolean;
  site: SiteWithLocations;
  /** The bin the action was opened from. Always one of the bins it attaches to. */
  binId: string | null;
  onHide: () => void;
  /** Called after the server stored the certificate; the caller reloads the list and says so. */
  onSaved: (message: string) => void;
}

const Required: React.FC = () => (
  <span className="tw-text-danger" aria-hidden="true">
    {' '}
    *
  </span>
);

function emptyForm(binId: string | null): CertificateForm {
  return { file: null, binIds: binId ? [binId] : [], description: '', reference: '', issuedAt: todayIso() };
}

/**
 * Attaches a quality certificate to a bin: the file, the date on it and a description. A bin
 * can carry several over time. The same certificate often covers other bins from the same
 * crushing run, so other bins at the site holding the same material can be ticked too: the file
 * is stored once and recorded against each.
 */
const AttachCertificateModal: React.FC<AttachCertificateModalProps> = ({ show, site, binId, onHide, onSaved }) => {
  const bins = useMemo(() => certifiableBins(site), [site]);
  const bin = bins.find((choice) => choice.id === binId) ?? null;
  const sameMaterial = useMemo(
    () => (bin ? bins.filter((choice) => choice.id !== bin.id && choice.materialId === bin.materialId) : []),
    [bins, bin]
  );

  const [form, setForm] = useState<CertificateForm>(() => emptyForm(binId));
  const [attempted, setAttempted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Open fresh each time, so a second upload does not carry the first one's file and date.
  useEffect(() => {
    if (!show) return;
    setForm(emptyForm(binId));
    setAttempted(false);
    setSaving(false);
    setSaveError(null);
  }, [show, binId]);

  const errors = validateCertificateForm(form);
  const shown = attempted ? errors : {};

  const update = (patch: Partial<CertificateForm>) => {
    setForm((current) => ({ ...current, ...patch }));
    setSaveError(null);
  };

  const toggleBin = (id: string, checked: boolean) =>
    update({ binIds: checked ? [...form.binIds, id] : form.binIds.filter((existing) => existing !== id) });

  const handleSave = async (event: React.FormEvent) => {
    event.preventDefault();
    setAttempted(true);
    if (Object.keys(errors).length > 0 || saving || !form.file) return;

    setSaving(true);
    setSaveError(null);
    try {
      await materialCertificateService.attach({
        file: form.file,
        binIds: form.binIds,
        description: form.description.trim(),
        reference: form.reference.trim(),
        issuedAt: form.issuedAt,
      });
      const labels = form.binIds.map((id) => bins.find((choice) => choice.id === id)?.label ?? 'a bin');
      onSaved(attachedMessage(form.description, labels));
      onHide();
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : 'Failed to attach the certificate');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={saving ? () => undefined : onHide}>
      <Modal.Header closeButton>
        <Modal.Title>Attach a quality certificate</Modal.Title>
        {bin ? (
          <p className="tw-mb-0 tw-mt-0.5 tw-text-sm tw-text-content-muted">
            {bin.label} · {bin.materialLabel}
          </p>
        ) : null}
      </Modal.Header>

      <form noValidate onSubmit={handleSave} className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col">
        <Modal.Body className="tw-space-y-4">
          <Form.Group controlId="certificate-file" className="tw-mb-0">
            <Form.Label>
              File
              <Required />
            </Form.Label>
            <Form.Control
              type="file"
              accept=".pdf,image/*"
              isInvalid={!!shown.file}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => update({ file: event.target.files?.[0] ?? null })}
            />
            {shown.file ? (
              <Form.Control.Feedback type="invalid">{shown.file}</Form.Control.Feedback>
            ) : (
              <Form.Text>A PDF or a photo of the report, up to 25 MB.</Form.Text>
            )}
          </Form.Group>

          <div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
            <Form.Group controlId="certificate-issued" className="tw-mb-0">
              <Form.Label>
                Date on the certificate
                <Required />
              </Form.Label>
              <Form.Control
                type="date"
                value={form.issuedAt}
                isInvalid={!!shown.issuedAt}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => update({ issuedAt: event.target.value })}
              />
              {shown.issuedAt ? <Form.Control.Feedback type="invalid">{shown.issuedAt}</Form.Control.Feedback> : null}
            </Form.Group>

            <Form.Group controlId="certificate-reference" className="tw-mb-0">
              <Form.Label>Report number</Form.Label>
              <Form.Control
                type="text"
                value={form.reference}
                maxLength={CERTIFICATE_REFERENCE_MAX_LENGTH}
                isInvalid={!!shown.reference}
                autoComplete="off"
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => update({ reference: event.target.value })}
              />
              {shown.reference ? <Form.Control.Feedback type="invalid">{shown.reference}</Form.Control.Feedback> : null}
            </Form.Group>
          </div>

          <Form.Group controlId="certificate-description" className="tw-mb-0">
            <Form.Label>
              Description
              <Required />
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={form.description}
              maxLength={CERTIFICATE_DESCRIPTION_MAX_LENGTH}
              placeholder="e.g. Sieve analysis - crushing run 14. Passes spec for 19mm surfacing."
              isInvalid={!!shown.description}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => update({ description: event.target.value })}
            />
            {shown.description ? <Form.Control.Feedback type="invalid">{shown.description}</Form.Control.Feedback> : null}
          </Form.Group>

          {sameMaterial.length > 0 ? (
            <fieldset className="tw-mb-0">
              <legend className="tw-mb-1 tw-text-sm tw-font-medium tw-text-content-secondary">
                Also attach to other bins holding this material
              </legend>
              <p className="tw-mb-2 tw-text-xs tw-text-content-muted">
                For bins filled from the same crushing run. Each gets its own copy.
              </p>
              <div className="tw-max-h-48 tw-space-y-1 tw-overflow-y-auto tw-rounded tw-border tw-border-line tw-p-3">
                {sameMaterial.map((other) => (
                  <Form.Check
                    key={other.id}
                    id={`certificate-bin-${other.id}`}
                    checked={form.binIds.includes(other.id)}
                    onChange={(event) => toggleBin(other.id, event.target.checked)}
                    label={other.label}
                  />
                ))}
              </div>
            </fieldset>
          ) : null}
          {shown.binIds ? <p className="tw-mb-0 tw-text-sm tw-text-danger">{shown.binIds}</p> : null}

          {saveError ? (
            <Alert variant="danger" className="tw-mb-0">
              {saveError}
            </Alert>
          ) : null}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onHide} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={saving}>
            {saving ? (
              <>
                <Spinner size="sm">Saving</Spinner>
                Attaching…
              </>
            ) : (
              'Attach certificate'
            )}
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};

export default AttachCertificateModal;
